<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Students Management
                </h1>
                <div class="flex space-x-3">
                    <button
                        @click="openImportModal"
                        class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Import Student
                    </button>
                    <button
                        @click="openModal"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <Plus class="w-4 h-4 mr-1" />
                        Add Student
                    </button>
                </div>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by name, NIS, or email..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Class Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.current_class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="All Classes"
                            option-label="name"
                            option-value="id"
                            @update:model-value="fetchStudents"
                        />
                    </div>

                    <PageLengthSelector
                        v-model="studentStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="studentStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="studentStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ studentStore.error }}
                        </p>
                        <button
                            @click="studentStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!studentStore.loading && !studentStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Student
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Student ID
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    NIS
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Class
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="student in studentStore.students"
                                :key="student.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="m-0">
                                            <div
                                                class="text-sm font-medium text-gray-900 dark:text-white"
                                            >
                                                {{
                                                    student.user?.name ||
                                                    "Unknown"
                                                }}
                                            </div>
                                            <div
                                                class="text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                {{
                                                    student.user?.email ||
                                                    "No email"
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ student.student_id }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ student.nisn }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ student.current_class?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editStudent(student)"
                                        title="Edit Student"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(student)"
                                        title="Delete Student"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="studentStore.students.length === 0"
                    class="text-center py-12"
                >
                    <GraduationCap class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No students found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new student.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Student
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (studentStore.meta?.total || 0) >
                    (studentStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="studentStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            studentStore.meta?.current_page ===
                            studentStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                paginationFrom
                            }}</span>
                            to
                            <span class="font-medium">{{ paginationTo }}</span>
                            of
                            <span class="font-medium">{{
                                paginationTotal
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    studentStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === studentStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    studentStore.meta?.current_page ===
                                    studentStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Student Modal -->
            <StudentModal
                v-if="showModal"
                :show-modal="showModal"
                :student="selectedStudent"
                :is-editing="isEditing"
                @close="closeModal"
                @save="handleSave"
                @saved="handleSaved"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />

            <!-- Import Student Modal -->
            <StudentImportModal
                v-if="showImportModal"
                @close="closeImportModal"
                @success="handleImportSuccess"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useStudentStore } from "@/stores/studentStore";
import { useClassSchoollStore } from "@/stores/classSchoollStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StudentModal from "@/components/StudentModal.vue";
import StudentImportModal from "@/components/StudentImportModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import type { Student } from "@/api/student";
import { Plus, RotateCcw, Pencil, Trash2, GraduationCap } from "@lucide/vue";

const studentStore = useStudentStore();
const classStore = useClassSchoollStore();
// State
const searchQuery = ref("");
const filters = ref({
    current_class_id: "",
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const showImportModal = ref(false);
const selectedStudent = ref<Student | null>(null);
const isEditing = ref(false);
const studentToDelete = ref<Student | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this student? This action cannot be undone.",
);

// Options for Class filter
const classOptions = computed(() => {
    const options = [{ id: "", name: "All Classes" }];
    classStore.classes.forEach((classItem: any) => {
        options.push({ id: classItem.id, name: classItem.name });
    });
    return options;
});

// Pagination computed properties
const paginationFrom = computed(() => {
    return (
        studentStore.meta?.from ??
        (studentStore.page - 1) * studentStore.perPage + 1
    );
});

const paginationTo = computed(() => {
    return (
        studentStore.meta?.to ??
        Math.min(
            studentStore.page * studentStore.perPage,
            studentStore.meta?.total || 0,
        )
    );
});

const paginationTotal = computed(() => {
    return studentStore.meta?.total ?? 0;
});

const visiblePages = computed(() => {
    const current = studentStore.meta?.current_page || 1;
    const last = studentStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.current_class_id !== "" ||
        studentStore.page !== 1 ||
        studentStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    studentStore.perPage = value;
    studentStore.page = 1;
    fetchStudents();
};

const fetchStudents = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await studentStore.fetchStudents(searchFilters);
};

const debouncedSearch = useDebounce(fetchStudents, 300);

const openModal = () => {
    selectedStudent.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editStudent = (student: Student) => {
    selectedStudent.value = student;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedStudent.value = null;
    isEditing.value = false;
};

const confirmDelete = (student: Student) => {
    studentToDelete.value = student;
    deleteModalTitle.value = "Delete Student";
    deleteModalMessage.value = `Are you sure you want to delete ${student.user?.name || "this student"}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    studentToDelete.value = null;
};

const handleSave = async () => {
    // This function is now handled within the modal
    // Validation errors are handled in modal and don't reach here
    // Only successful operations will trigger to 'saved' event
};

const handleSaved = () => {
    // Refresh the students list to show new/updated student
    fetchStudents();
    showNotification(
        isEditing.value
            ? "Student updated successfully"
            : "Student created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (studentToDelete.value) {
            await studentStore.deleteStudent(studentToDelete.value.id);
            showNotification("Student deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete student", "error");
    }
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Students imported successfully", "success");
    fetchStudents();
};

const previousPage = () => {
    if ((studentStore.meta?.current_page || 1) > 1) {
        studentStore.setPage((studentStore.meta?.current_page || 1) - 1);
        fetchStudents();
    }
};

const nextPage = () => {
    if (
        (studentStore.meta?.current_page || 1) <
        (studentStore.meta?.last_page || 1)
    ) {
        studentStore.setPage((studentStore.meta?.current_page || 1) + 1);
        fetchStudents();
    }
};

const goToPage = (page: number) => {
    studentStore.setPage(page);
    fetchStudents();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value.current_class_id = "";
    studentStore.resetFilters();
    fetchStudents();
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    classStore.fetchClasses();
    fetchStudents();
});
</script>
